/**
 * Страница шаблонов сообщений: список, создание, редактирование, превью с переменными
 * Дата: 28.12.2025 - Реализовано для TODO-013 (Frontend страницы + WhatsApp интеграция)
 * Дата: 30.12.2025 - Реализовано TODO-025: добавлены переключатель активации, выбор канала отправки, исправлено редактирование
 * Дата: 30.12.2025 - Реализовано TODO-027: добавлен выбор Regular/WABA, WABA Template Selector с маппингом переменных
 * Дата: 26.01.2026 - Добавлено frontend логирование (TODO-034)
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/templates.ts - API для работы с шаблонами
 * - frontend/src/hooks/useTemplates.ts - хук для управления состоянием шаблонов
 * - internal/handler/template.go - бэкенд обработчик шаблонов
 * - components/ui/SearchInput.tsx - унифицированный поиск
 * - components/ui/FilterSelect.tsx - унифицированный фильтр
 * - components/ui/StatusBadge.tsx - унифицированный статус
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * - components/ui/ActionButtons.tsx - унифицированные кнопки действий
 * 
 * КАК РАБОТАЕТ:
 * 1. Загружает список шаблонов через useTemplates(tenantIdForApi)
 * 2. Фильтрует по поисковому запросу, типу, каналу и активности
 * 3. Отображает таблицу с действиями (просмотр, редактирование, удаление, активация/деактивация)
 * 4. Модальные окна для создания/редактирования шаблонов с поддержкой WABA
 * 5. Массовые операции активации/деактивации
 * 
 * СХЕМА:
 * TemplatesPage → useTemplates → templatesApi → backend API
 * TemplatesPage → SearchInput → фильтрация filteredTemplates
 * TemplatesPage → FilterSelect → фильтрация по типу/каналу
 * TemplatesPage → ActionButtons → handlePreview/handleDelete/openEditModal/handleToggleActive
 * TemplatesPage → Modal → handleCreate/handleUpdate
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button, LoadingState, Modal, TenantSelector } from '@/components/ui';
import { SubsectionSwitch } from '@/components/ui/SubsectionSwitch';
import { FlowNote } from '@/features/monitoring/FlowNote';
import {
  findTemplateSection,
  templateSectionIds,
  templateSectionRegistry,
  type TemplateSectionId,
} from '@/features/templates/templateSections';
import { getEventBindingCoverage } from '@/components/features/templates/bindingStatus';
import { asDispatchMode, type DispatchMode } from '@/features/templates/dispatchMode';
import { useSubsectionParam } from '@/hooks/useSubsectionParam';
import { useTemplates } from '@/hooks/useTemplates';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from '@/hooks/useToast';
import { usePermissions } from '@/hooks/usePermissions';
import { Can } from '@/components/Can';
import { Plus } from 'lucide-react';
import {
  templatesApi,
  Template,
  TemplateVariable,
  WABATemplate,
  EventTemplateBindingItem,
} from '@/api/templates';
import { serviceClustersApi } from '@/api/service-clusters';
import { tenantsApi } from '@/api/tenants';
import { settingsApi } from '@/api/settings';
import { autoRepliesApi, AutoReplyRule } from '@/api/auto-replies';
import { fetchAllPages } from '@/api/paging';
import { ClusterFollowupBindingsOverviewCard } from '@/components/features/templates/ClusterFollowupBindingsOverviewCard';
import { EventBindingsCard } from '@/components/features/templates/EventBindingsCard';
import { EventBindingsRuntimeCard } from '@/components/features/templates/EventBindingsRuntimeCard';
import { TemplateFormModal } from '@/components/features/templates/TemplateFormModal';
import { TemplatesFilterBar } from '@/components/features/templates/TemplatesFilterBar';
import { TemplatesTable } from '@/components/features/templates/TemplatesTable';
import { ResolvedTemplatePreview } from '@/components/features/templates/ResolvedTemplatePreview';
import { filterTemplatesByChannel } from '@/config/channels';
import {
  collectCoreTemplateIds,
  templateCatalogIds,
  type TemplateCatalogId,
} from '@/features/templates/coreBroadcastCatalog';
import { isWabaBackedTemplate, matchesTemplateChannelFilter } from '@/features/templates/templateTaxonomy';
import {
  countTemplateUsage,
  getTemplateUsageMeta,
  matchesTemplateUsageFilter,
  type TemplateClusterUsage,
  type TemplateUsageFilter,
} from '@/features/templates/templateUsage';
import type { ServiceCluster, ServiceClusterTemplate } from '@/api/service-clusters';

export function TemplatesPage() {
  const { tenantId: tenantIdForApi, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  const { showSuccess, showError } = useToast();
  const { can } = usePermissions();
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [previewingTemplate, setPreviewingTemplate] = useState<string | null>(null);
  const [sectionId, setSectionId] = useSubsectionParam('section', templateSectionIds, 'list');
  // 09.10.26. Основные — пять рассылок, дополнительные — остальные шаблоны клиники.
  const [catalogId, setCatalogId] = useSubsectionParam('catalog', templateCatalogIds, 'core');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [messengerFilter, setMessengerFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>(''); // 02.02.2026 - Фильтр по категории
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all'); // 26.01.2026 - TODO-036: Фильтр активности
  // 16.03.2026 - ШАБЛОНЫ: сценарий использования шаблона фильтруем отдельно от статуса
  const [usageFilter, setUsageFilter] = useState<TemplateUsageFilter>('');
  const [showTemplateGuide, setShowTemplateGuide] = useState(false);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [tenantsList, setTenantsList] = useState<{ id: string; name: string }[]>([]);
  // 16.03.2026 - ШАБЛОНЫ: собираем обратную карту template -> cluster links, чтобы корректно считать cluster/event-driven usage
  const [clusterUsageByTemplate, setClusterUsageByTemplate] = useState<Record<string, TemplateClusterUsage>>({});
  const [clustersForBindings, setClustersForBindings] = useState<ServiceCluster[]>([]);
  const [clusterUsageLoading, setClusterUsageLoading] = useState(false);
  
  // Дата: 26.01.2026 - Массовый выбор шаблонов
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  
  // Дата: 26.01.2026 - Глобальный killswitch
  const [messagingEnabled, setMessagingEnabled] = useState<boolean>(true);
  const [broadcastsEnabled, setBroadcastsEnabled] = useState<boolean>(true);
  
  // WABA Template Selector (TODO-027)
  const [whatsappType, setWhatsappType] = useState<'regular' | 'waba'>('regular');
  const [wabaTemplates, setWabaTemplates] = useState<WABATemplate[]>([]);
  const [selectedWabaTemplate, setSelectedWabaTemplate] = useState<WABATemplate | null>(null);
  const [wabaVariableMapping, setWabaVariableMapping] = useState<Record<string, { type: 'static' | 'dynamic'; value?: string; field?: string }>>({});
  const [isLoadingWabaTemplates, setIsLoadingWabaTemplates] = useState(false);
  
  // Дата: 30.01.2026 - Связанные автоответы для визуализации цепочек
  const [autoReplies, setAutoReplies] = useState<AutoReplyRule[]>([]);
  // Дата: 05.03.2026 - Привязки event_type -> template для Event-Driven pipeline
  const [eventBindings, setEventBindings] = useState<EventTemplateBindingItem[]>([]);
  const [clusterFollowupDispatchMode, setClusterFollowupDispatchMode] = useState<DispatchMode | null>(null);
  const [eventBindingsLoading, setEventBindingsLoading] = useState(false);
  const [eventBindingSavingByType, setEventBindingSavingByType] = useState<Record<string, boolean>>({});
	const { templates, total, isLoading, createTemplate, updateTemplate, deleteTemplate, previewTemplate, isCreating, isUpdating, previewData, isPreviewing } = useTemplates(tenantIdForApi, {
		search: searchQuery,
	});

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'templates' });
  }, []);

  useEffect(() => {
    templatesApi.getVariables().then((data) => setVariables(data.variables || [])).catch(() => {});
    if (isSuperAdmin) {
      tenantsApi.list().then((data) => setTenantsList(data.tenants || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Дата: 30.01.2026 - Загрузка автоответов для визуализации связей
  useEffect(() => {
    // 09.10.26. Не удалять по требованию владельца системы. Иначе связи шаблон↔правило обрезаются 20 строками.
    fetchAllPages((offset, limit) =>
      autoRepliesApi.listRules(tenantIdForApi, undefined, { offset, limit }).then((data) => ({
        items: data.rules || [],
        total: data.total,
      })),
    )
      .then((page) => setAutoReplies(page.items))
      .catch(() => setAutoReplies([]));
  }, [tenantIdForApi]);

  // Дата: 26.01.2026 - Загрузка глобального killswitch
  useEffect(() => {
    if (tenantIdForApi) {
      settingsApi.getDeliverySettings(tenantIdForApi)
        .then((data) => {
          setMessagingEnabled(data.messaging_enabled !== false);
          setBroadcastsEnabled(data.broadcasts_enabled !== false);
        })
        .catch(() => {});
    }
  }, [tenantIdForApi]);

  // Логирование загрузки шаблонов
  useEffect(() => {
    if (!isLoading && templates) {
      logger.info('templates_list_loaded', { 
        count: templates.length,
        tenant_id: tenantIdForApi 
      });
    }
  }, [isLoading, templates, tenantIdForApi]);

  // Дата: 05.03.2026 - Загрузка привязок event_type -> template
  useEffect(() => {
    if (!tenantIdForApi) {
      setEventBindings([]);
      setClusterFollowupDispatchMode(null);
      return;
    }
    setEventBindingsLoading(true);
    templatesApi.listEventBindings(tenantIdForApi)
      .then((data) => {
        setEventBindings(data.bindings || []);
        setClusterFollowupDispatchMode(asDispatchMode(data.cluster_followup_dispatch_mode));
      })
      .catch(() => {
        setEventBindings([]);
        setClusterFollowupDispatchMode(null);
      })
      .finally(() => setEventBindingsLoading(false));
  }, [tenantIdForApi]);

  const buildActiveClusterUsageLinks = useCallback(
    (cluster: ServiceCluster, clusterTemplates: ServiceClusterTemplate[]): ServiceClusterTemplate[] =>
      clusterTemplates
        .filter((link) => link.is_active)
        .map((link) => ({
          ...link,
          id: link.id || `${cluster.id}:${link.trigger_type}:${link.channel || 'generic'}:${link.template_id}`,
          cluster_id: link.cluster_id || cluster.id,
          dedup_scope: link.dedup_scope || 'per_appointment',
        })),
    []
  );

  const refreshClusterUsage = useCallback(async () => {
    if (!tenantIdForApi) {
      setClustersForBindings([]);
      setClusterUsageByTemplate({});
      setClusterUsageLoading(false);
      return;
    }

    setClusterUsageLoading(true);
    try {
      const clustersData = await serviceClustersApi.list(true, tenantIdForApi);
      const clusters = clustersData.clusters || [];
      const templateLinks = await Promise.all(
        clusters.map(async (cluster) => {
          try {
            const data = await serviceClustersApi.getTemplates(cluster.id, tenantIdForApi);
            return { cluster, templates: buildActiveClusterUsageLinks(cluster, data.templates || []) };
          } catch {
            return { cluster, templates: [] };
          }
        })
      );

      const nextMap: Record<string, TemplateClusterUsage> = {};
      templateLinks.forEach(({ cluster, templates }) => {
        templates.forEach((link) => {
          if (!nextMap[link.template_id]) {
            nextMap[link.template_id] = { clusters: [], links: [] };
          }
          if (!nextMap[link.template_id].clusters.some((item) => item.id === cluster.id)) {
            nextMap[link.template_id].clusters.push(cluster);
          }
          nextMap[link.template_id].links.push(link);
        });
      });

      setClustersForBindings(clusters);
      setClusterUsageByTemplate(nextMap);
    } catch {
      setClustersForBindings([]);
      setClusterUsageByTemplate({});
    } finally {
      setClusterUsageLoading(false);
    }
  }, [buildActiveClusterUsageLinks, tenantIdForApi]);

  useEffect(() => {
    refreshClusterUsage().catch(() => {});
  }, [refreshClusterUsage]);

  // Маппинг tenant_id → tenant_name для отображения
  const getTenantName = (tenantId: string) => {
    const tenant = tenantsList.find(t => t.id === tenantId);
    return tenant?.name || tenantId;
  };

  const templateEventTypesById = useMemo(() => {
    const map = new Map<string, Set<string>>();
    eventBindings.forEach((binding) => {
      const slots = [binding.generic_binding, ...(binding.channel_bindings || [])];
      slots.forEach((slot) => {
        if (!slot.template?.id) {
          return;
        }
        if (!map.has(slot.template.id)) {
          map.set(slot.template.id, new Set<string>());
        }
        map.get(slot.template.id)?.add(binding.event_type);
      });
    });
    return map;
  }, [eventBindings]);

  const getRelatedAutoReplies = useCallback((template: Template) => {
    const eventTypes = templateEventTypesById.get(template.id) || new Set<string>();
    const relationBuckets = new Map<string, { key: string; label: string; rules: AutoReplyRule[] }>();

    const addRelation = (key: string, label: string, rule: AutoReplyRule) => {
      if (!relationBuckets.has(key)) {
        relationBuckets.set(key, { key, label, rules: [] });
      }
      relationBuckets.get(key)?.rules.push(rule);
    };

    autoReplies.forEach((rule) => {
      if (rule.template_id === template.id) {
        addRelation('response', 'ответ', rule);
      }
      if (rule.waba_response_template_id === template.id) {
        addRelation('waba_response', 'WABA ответ', rule);
      }
      if (rule.waba_trigger_template_id === template.id) {
        addRelation('waba_trigger', 'WABA trigger', rule);
      }
      if (rule.tg_trigger_template_id === template.id) {
        addRelation('tg_trigger', 'TG/MAX trigger', rule);
      }
      if (rule.after_event_type && eventTypes.has(rule.after_event_type)) {
        addRelation('context', 'контекст', rule);
      }
    });

    const order = ['context', 'response', 'waba_response', 'waba_trigger', 'tg_trigger'];
    return order
      .map((key) => relationBuckets.get(key))
      .filter((item): item is { key: string; label: string; rules: AutoReplyRule[] } => Boolean(item))
      .map((item) => ({
        key: item.key,
        label: item.rules.length > 1 ? `${item.label}×${item.rules.length}` : item.label,
        title: `${item.label}: ${item.rules.map((rule) => rule.name).join(', ')}`,
        is_active: item.rules.some((rule) => rule.is_active),
      }));
  }, [autoReplies, templateEventTypesById]);

  const eventBoundTemplateIds = useMemo(() => {
    const ids = new Set<string>();

    eventBindings.forEach((binding) => {
      const slots = [binding.generic_binding, ...(binding.channel_bindings || [])];
      slots.forEach((slot) => {
        if (slot.template?.id) {
          ids.add(slot.template.id);
        }
      });
    });

    return ids;
  }, [eventBindings]);

  const templatesById = useMemo(
    () =>
      Object.fromEntries(
        templates.map((template) => [template.id, template])
      ) as Record<string, Template>,
    [templates]
  );

  const autoReplyTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    autoReplies.forEach((rule) => {
      if (rule.template_id) {
        ids.add(rule.template_id);
      }
      if (rule.waba_response_template_id) {
        ids.add(rule.waba_response_template_id);
      }
    });
    return ids;
  }, [autoReplies]);

  const resolveTemplateUsageMeta = useCallback(
    (template: Template) => getTemplateUsageMeta(template, clusterUsageByTemplate, eventBoundTemplateIds, autoReplyTemplateIds),
    [autoReplyTemplateIds, clusterUsageByTemplate, eventBoundTemplateIds],
  );

  const [formData, setFormData] = useState<Partial<Template>>({
    name: '',
    content: '',
    variables: [],
    messenger: '',
    template_type: 'text',
    is_active: true,
    tenant_id: isSuperAdmin ? (selectedTenantId || undefined) : undefined,
    buttons: [],
    text_alternative: '',
    send_schedule: undefined,
    okdoc_scenario_profile: undefined,
  });

  // 12.02.2026 — Флаг: показывать WABA секцию при автовыборе (опционально)
  const [showWabaForAll, setShowWabaForAll] = useState(false);
  const [wabaLoadError, setWabaLoadError] = useState('');

  // Загрузить WABA шаблоны при выборе WhatsApp/WABA ИЛИ при автовыборе с включённой WABA секцией
  const shouldLoadWaba = (formData.messenger === 'whatsapp' && whatsappType === 'waba') || 
    ((!formData.messenger || formData.messenger === '' || formData.messenger === 'all') && showWabaForAll);

  const wabaTenantId = (isSuperAdmin && formData.tenant_id) ? formData.tenant_id : tenantIdForApi;

  useEffect(() => {
    if (!(shouldLoadWaba && wabaTenantId)) {
      setWabaTemplates([]);
      setWabaLoadError('');
      setSelectedWabaTemplate(null);
      setWabaVariableMapping({});
      return;
    }
    let cancelled = false;
    setIsLoadingWabaTemplates(true);
    setWabaLoadError('');
    templatesApi.getWABATemplates(wabaTenantId)
      .then((data) => {
        if (cancelled) return;
        setWabaTemplates(data.templates || []);
        setIsLoadingWabaTemplates(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err.response?.data?.error || err.message || 'сеть';
        setWabaTemplates([]);
        setWabaLoadError(message);
        setIsLoadingWabaTemplates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldLoadWaba, wabaTenantId]);

  useEffect(() => {
    if (!isWabaBackedTemplate(formData) || !formData.external_template_id || wabaTemplates.length === 0) {
      return;
    }

    const matchedTemplate = wabaTemplates.find(
      (template) =>
        template.template_guid === formData.external_template_id ||
        template.id === formData.external_template_id
    );

    if (matchedTemplate && selectedWabaTemplate?.id !== matchedTemplate.id) {
      // 15.03.26 - При открытии edit WABA шаблон должен подняться как WABA, а не как regular WhatsApp.
      setSelectedWabaTemplate(matchedTemplate);
    }
  }, [formData, isWabaBackedTemplate, selectedWabaTemplate?.id, wabaTemplates]);

  const handleCreate = async () => {
    logger.info('template_create_click', { 
      template_type: formData.template_type,
      messenger: formData.messenger,
      whatsapp_type: whatsappType 
    });
    try {
      const result = await createTemplate(formData);
      logger.info('template_created', { 
        template_id: result.id,
        template_name: result.name,
        template_type: result.template_type 
      });
      showSuccess('Шаблон создан');
      setIsCreateModalOpen(false);
      resetForm();
    } catch (error: any) {
      logger.error('template_create_failed', { 
        error: error.response?.data?.error || error.message,
        template_type: formData.template_type 
      });
      showError(error.response?.data?.error || 'Ошибка при создании шаблона');
    }
  };

  const handleUpdate = async () => {
    if (!editingTemplate) return;
    logger.info('template_update_click', { template_id: editingTemplate });
    try {
      await updateTemplate({ id: editingTemplate, data: formData, tenantId: tenantIdForApi });
      logger.info('template_updated', { template_id: editingTemplate });
      showSuccess('Шаблон обновлен');
      setEditingTemplate(null);
      resetForm();
    } catch (error: any) {
      logger.error('template_update_failed', { 
        template_id: editingTemplate,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при обновлении шаблона');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот шаблон?')) return;
    logger.info('template_delete_click', { template_id: id });
    try {
      await deleteTemplate({ id, tenantId: tenantIdForApi });
      logger.info('template_deleted', { template_id: id });
      showSuccess('Шаблон удален');
    } catch (error: any) {
      logger.error('template_delete_failed', { 
        template_id: id,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при удалении шаблона');
    }
  };

  const handlePreview = async (id: string) => {
    setPreviewingTemplate(id);
    logger.info('template_preview_click', { template_id: id });
    try {
      const testVars: Record<string, string> = {};
      const template = templates.find(t => t.id === id);
      if (template) {
        template.variables.forEach(v => {
          testVars[v] = `[${v}]`;
        });
      }
      await previewTemplate({
        id,
        variables: testVars,
        tenantId: tenantIdForApi,
        channel: template?.messenger || template?.channel || undefined,
      });
      logger.info('template_preview_success', { template_id: id });
    } catch (error: any) {
      logger.error('template_preview_failed', { 
        template_id: id,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при превью шаблона');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      content: '',
      variables: [],
      messenger: '',
      template_type: 'text',
      is_active: true,
      tenant_id: isSuperAdmin ? (selectedTenantId || undefined) : undefined,
      buttons: [],
      text_alternative: '',
      send_schedule: undefined,
      okdoc_scenario_profile: undefined,
    });
    setWhatsappType('regular');
    setSelectedWabaTemplate(null);
    setWabaVariableMapping({});
  };

  const openEditModal = (template: Template) => {
    const isWabaTemplate = isWabaBackedTemplate(template);
    setEditingTemplate(template.id);
    setFormData({
      name: template.name,
      content: template.content,
      variables: template.variables,
      messenger: template.messenger === 'all' ? '' : (template.messenger || ''),
      template_type: template.template_type,
      category: template.category || undefined,
      is_active: template.is_active,
      tenant_id: template.tenant_id || selectedTenantId || undefined,
      buttons: template.buttons || [],
      text_alternative: template.text_alternative || '',
      send_schedule: template.send_schedule || undefined,
      okdoc_scenario_profile: template.okdoc_scenario_profile,
      external_template_id: template.external_template_id,
      waba_variables_mapping: template.waba_variables_mapping,
    });
    setWhatsappType(isWabaTemplate ? 'waba' : 'regular');
    setShowWabaForAll(false);
    if (!isWabaTemplate) {
      setSelectedWabaTemplate(null);
      setWabaVariableMapping({});
    }
    
    // Для SuperAdmin: установить selectedTenantId если ещё не выбран
    if (isSuperAdmin && !selectedTenantId && template.tenant_id) {
      setSelectedTenant(template.tenant_id);
    }
  };

  const insertVariable = (code: string) => {
    const variable = `{{${code}}}`;
    setFormData({ ...formData, content: (formData.content || '') + variable });
  };

  const previewTemplateEntity = useMemo(
    () => templates.find((template) => template.id === previewingTemplate) || null,
    [previewingTemplate, templates]
  );

  const handleToggleActive = async (id: string, isActive: boolean) => {
    const action = isActive ? 'активировать' : 'деактивировать';
    if (!confirm(`Вы уверены, что хотите ${action} этот шаблон?`)) return;
    logger.info('template_toggle_active_click', { template_id: id, is_active: isActive });
    try {
      await updateTemplate({ 
        id, 
        data: { is_active: isActive }, 
        tenantId: tenantIdForApi 
      });
      logger.info('template_toggle_active_success', { template_id: id, is_active: isActive });
      showSuccess(isActive ? 'Шаблон активирован' : 'Шаблон деактивирован');
    } catch (error: any) {
      logger.error('template_toggle_active_failed', { 
        template_id: id,
        is_active: isActive,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при изменении статуса');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Массовые операции (Дата: 26.01.2026)
  // ═══════════════════════════════════════════════════════════════
  
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredTemplates.map(t => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedIds(newSet);
  };

  const handleBulkActivate = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Активировать ${selectedIds.size} шаблон(ов)?`)) return;
    
    setIsBulkLoading(true);
    logger.info('template_bulk_activate_click', { count: selectedIds.size });
    
    try {
      const result = await templatesApi.bulkActivate(Array.from(selectedIds), tenantIdForApi);
      logger.info('template_bulk_activate_success', { requested: selectedIds.size, updated: result.updated });
      showSuccess(`Активировано: ${result.updated} шаблон(ов)`);
      setSelectedIds(new Set());
      // Обновить список
      window.location.reload();
    } catch (error: any) {
      logger.error('template_bulk_activate_failed', { error: error.message });
      showError(error.response?.data?.error || 'Ошибка массовой активации');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Деактивировать ${selectedIds.size} шаблон(ов)?`)) return;
    
    setIsBulkLoading(true);
    logger.info('template_bulk_deactivate_click', { count: selectedIds.size });
    
    try {
      const result = await templatesApi.bulkDeactivate(Array.from(selectedIds), tenantIdForApi);
      logger.info('template_bulk_deactivate_success', { requested: selectedIds.size, updated: result.updated });
      showSuccess(`Деактивировано: ${result.updated} шаблон(ов)`);
      setSelectedIds(new Set());
      // Обновить список
      window.location.reload();
    } catch (error: any) {
      logger.error('template_bulk_deactivate_failed', { error: error.message });
      showError(error.response?.data?.error || 'Ошибка массовой деактивации');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const coreTemplateIds = useMemo(
    () => collectCoreTemplateIds(eventBindings),
    [eventBindings],
  );

	const filteredTemplates = templates.filter((template) => {
    // 16.03.2026 - ШАБЛОНЫ: один шаблон может быть одновременно manual/event-driven/cluster + active/inactive
    const usage = resolveTemplateUsageMeta(template);
    const isCore = coreTemplateIds.has(template.id);
    const matchesCatalog = eventBindingsLoading || (catalogId === 'extra' ? !isCore : isCore);
    const matchesType =
      !typeFilter ||
      (typeFilter === 'waba_template' && isWabaBackedTemplate(template)) ||
      (typeFilter === 'text' && !isWabaBackedTemplate(template));
    const matchesMessenger = matchesTemplateChannelFilter(template, messengerFilter);
    const matchesCategory = !categoryFilter || template.category === categoryFilter;
    const matchesActive =
      activeFilter === 'all' ||
      (activeFilter === 'active' && template.is_active) ||
      (activeFilter === 'inactive' && !template.is_active);

		return matchesCatalog
      && matchesType
      && matchesMessenger
      && matchesCategory
      && matchesActive
      && matchesTemplateUsageFilter(usage, usageFilter);
    // 09.10.26. Активные всегда выше, неактивные собираются в конце. Порядок внутри группы сохраняется.
  }).sort((left, right) => Number(right.is_active) - Number(left.is_active));

  const templateUsageCounts = useMemo(
    () => countTemplateUsage(templates, clusterUsageByTemplate, eventBoundTemplateIds, autoReplyTemplateIds),
    [autoReplyTemplateIds, clusterUsageByTemplate, eventBoundTemplateIds, templates],
  );

  const activeTemplatesCount = useMemo(
    () => templates.filter((template) => template.is_active).length,
    [templates]
  );

  const inactiveTemplatesCount = templates.length - activeTemplatesCount;

  const eventDrivenTemplateOptions = templates
    .filter((t) => t.category !== 'manual')
    .sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return a.name.localeCompare(b.name, 'ru');
    });

  const refreshEventBindings = async () => {
    if (!tenantIdForApi) return;
    const data = await templatesApi.listEventBindings(tenantIdForApi);
    setEventBindings(data.bindings || []);
    setClusterFollowupDispatchMode(asDispatchMode(data.cluster_followup_dispatch_mode));
  };

  const getBindingKey = (eventType: string, channel?: string) => `${eventType}:${channel || 'generic'}`;

  const setBindingSaving = (eventType: string, channel: string | undefined, value: boolean) => {
    const key = getBindingKey(eventType, channel);
    setEventBindingSavingByType((prev) => ({ ...prev, [key]: value }));
  };

  const getBindingOptions = (channel?: string) => {
    if (!channel) {
      return eventDrivenTemplateOptions;
    }

    // 14.03.2026 — bindings UI теперь использует тот же строгий channel registry,
    // что и manual-broadcasts/create. Это убирает путаницу, когда в override слоте
    // WABA/Regular/SMS/TG показывались чужие шаблоны и оператор мог сохранить
    // привязку, которая заведомо не соответствует каналу.
    return filterTemplatesByChannel(eventDrivenTemplateOptions, channel);
  };

  const handleSetEventBinding = async (eventType: string, templateId: string, channel?: string) => {
    if (!tenantIdForApi || !templateId) return;
    try {
      setBindingSaving(eventType, channel, true);
      const result = await templatesApi.setEventBinding(eventType, templateId, channel, tenantIdForApi, false);
      showSuccess(result.message || 'Привязка сохранена');
      if (result.warnings && result.warnings.length > 0) {
        showError(result.warnings.join('\n'));
      }
      await refreshEventBindings();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Ошибка сохранения привязки');
    } finally {
      setBindingSaving(eventType, channel, false);
    }
  };

  const handleClearEventBinding = async (eventType: string, channel?: string) => {
    if (!tenantIdForApi) return;
    try {
      setBindingSaving(eventType, channel, true);
      const result = await templatesApi.clearEventBinding(eventType, channel, tenantIdForApi);
      showSuccess(result.message || 'Привязка очищена');
      await refreshEventBindings();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Ошибка очистки привязки');
    } finally {
      setBindingSaving(eventType, channel, false);
    }
  };

  const handleOpenEventBindingTemplate = (templateId: string) => {
    const template = templates.find((item) => item.id === templateId);
    if (!template) {
      showError('Не удалось найти шаблон в текущем списке');
      return;
    }
    openEditModal(template);
  };

  const dispatchModesByTemplateId = useMemo(() => {
    const map = new Map<string, Set<DispatchMode>>();
    const add = (templateId: string | undefined, mode: string | null | undefined) => {
      const resolved = asDispatchMode(mode);
      if (!templateId || !resolved) {
        return;
      }
      const current = map.get(templateId) ?? new Set<DispatchMode>();
      current.add(resolved);
      map.set(templateId, current);
    };

    eventBindings.forEach((binding) => {
      const slots = [binding.generic_binding, ...(binding.channel_bindings || [])];
      slots.forEach((slot) => {
        add(slot.template?.id, binding.dispatch_mode);
        add(slot.runtime?.template?.id, binding.dispatch_mode);
      });
    });

    Object.entries(clusterUsageByTemplate).forEach(([templateId, usage]) => {
      (usage.links || []).forEach((link) => {
        if (link.trigger_type === 'manual') {
          add(templateId, 'manual');
          return;
        }
        add(templateId, clusterFollowupDispatchMode);
      });
    });

    return map;
  }, [clusterFollowupDispatchMode, clusterUsageByTemplate, eventBindings]);

  const getDispatchModes = useCallback(
    (template: Template): DispatchMode[] => {
      const modes = dispatchModesByTemplateId.get(template.id);
      return (['auto', 'manual'] as const).filter((mode) => modes?.has(mode));
    },
    [dispatchModesByTemplateId],
  );

  const getScenarioBadges = (template: Template) => {
    const usage = resolveTemplateUsageMeta(template);
    const badges: Array<{ key: string; label: string; variant: 'secondary' | 'success' | 'warning' | 'info'; title?: string }> = [];

    if (usage.isManual) {
      badges.push({ key: 'manual', label: 'Ручной', variant: 'secondary' });
    }
    if (usage.isEventDriven) {
      badges.push({ key: 'event-driven', label: 'Event-Driven', variant: 'success' });
    }
    if (usage.isScheduled) {
      badges.push({ key: 'scheduled', label: 'По расписанию', variant: 'warning' });
    }
    if (usage.isCluster) {
      badges.push({
        key: 'cluster',
        label: usage.linkedClusters.length > 1 ? `Кластерный (${usage.linkedClusters.length})` : 'Кластерный',
        variant: 'info',
        title: usage.linkedClusters.map((cluster) => cluster.name).join(', '),
      });
    }
    if (usage.isPostProcedure) {
      badges.push({ key: 'post-procedure', label: 'После процедур', variant: 'warning' });
    }
    if (usage.isReplyChain) {
      badges.push({ key: 'reply-chain', label: 'Auto-Reply', variant: 'secondary' });
    }

    return badges;
  };

  const hasActiveFilters =
    !!searchQuery ||
    !!typeFilter ||
    !!messengerFilter ||
    !!categoryFilter ||
    usageFilter !== '' ||
    activeFilter !== 'all';

  const isTemplateFormOpen = isCreateModalOpen || !!editingTemplate;
  const isTemplateFormSubmitDisabled =
    isCreating ||
    isUpdating ||
    !formData.name ||
    !formData.content ||
    (isSuperAdmin && !editingTemplate && !formData.tenant_id);

  const handleCloseTemplateForm = () => {
    setIsCreateModalOpen(false);
    setEditingTemplate(null);
    resetForm();
  };

  const coreTemplatesCount = templates.filter((template) => coreTemplateIds.has(template.id)).length;
  const extraTemplatesCount = Math.max(templates.length - coreTemplatesCount, 0);
  const catalogItems = [
    { id: 'core', label: 'Основные', meta: String(coreTemplatesCount) },
    { id: 'extra', label: 'Дополнительные', meta: String(extraTemplatesCount) },
  ];

  const section = findTemplateSection(sectionId);
  const problematicBindingCount = eventBindings.filter(
    (binding) => getEventBindingCoverage(binding).isProblematic,
  ).length;
  const sectionItems = templateSectionRegistry.map((item) => ({
    id: item.id,
    label: item.label,
    meta: item.id === 'list'
      ? String(total)
      : item.id === 'event' && problematicBindingCount > 0
        ? String(problematicBindingCount)
        : undefined,
  }));

  return (
    <MainLayout
      headerTitle="Шаблоны"
      disableMainScroll
      headerActions={
        <Can permission="templates:create">
          <Button onClick={() => setIsCreateModalOpen(true)} testId="btn-create-template">
            <Plus className="mr-2 h-4 w-4" />
            Создать шаблон
          </Button>
        </Can>
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-3 border-b border-shell-border bg-shell-surface px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm leading-5 text-shell-muted">{section.lead}</p>
            <button type="button" className="shell-button-secondary h-9" onClick={() => setShowTemplateGuide((open) => !open)}>
              Справка
            </button>
          </div>
          {isSuperAdmin && (
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                showLabel={false}
                testId="select-tenant-filter"
              />
            </div>
          )}
          <SubsectionSwitch
            items={sectionItems}
            value={sectionId}
            onChange={(id) => setSectionId(id as TemplateSectionId)}
            testId="templates-section"
          />
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {showTemplateGuide && <FlowNote id="template" />}
        {!messagingEnabled && (
          <div className="rounded-2xl bg-shell-danger-soft border border-shell-danger/30 text-shell-danger px-4 py-3 relative" role="alert">
            <strong className="font-bold">⚠️ Рассылки отключены!</strong>
            <span className="block sm:inline ml-2">
              Отправка сообщений деактивирована на уровне конфигурации (MESSAGING_ENABLED=false). 
              Шаблоны можно редактировать, но рассылки не будут выполняться.
            </span>
          </div>
        )}
        {messagingEnabled && !broadcastsEnabled && (
          <div className="rounded-2xl bg-shell-warning-soft border border-shell-warning/30 text-shell-warning px-4 py-3 relative" role="alert">
            <strong className="font-bold">⚠️ Рассылки отключены для клиники</strong>
            <span className="block sm:inline ml-2">
              Отправка сообщений деактивирована для этой клиники. 
              Включите в настройках клиники.
            </span>
          </div>
        )}

        {sectionId === 'list' && (
        <SubsectionSwitch
          items={catalogItems}
          value={catalogId}
          onChange={(id) => setCatalogId(id as TemplateCatalogId)}
          testId="templates-catalog"
        />
        )}

        {sectionId === 'list' && (
        <TemplatesFilterBar
          isSuperAdmin={isSuperAdmin}
          showTenantSelector={false}
          selectedTenantId={selectedTenantId || ''}
          onTenantChange={setSelectedTenant}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          messengerFilter={messengerFilter}
          onMessengerFilterChange={setMessengerFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          activeFilter={activeFilter}
          onActiveFilterChange={setActiveFilter}
          usageFilter={usageFilter}
          onUsageFilterChange={setUsageFilter}
          templatesCount={total}
          activeTemplatesCount={activeTemplatesCount}
          inactiveTemplatesCount={inactiveTemplatesCount}
          templateUsageCounts={templateUsageCounts}
        />
        )}

        {sectionId === 'event' && (
        <EventBindingsCard
          embedded
          tenantIdForApi={tenantIdForApi}
          isCollapsed={false}
          onToggleCollapse={() => undefined}
          eventBindings={eventBindings}
          eventBindingsLoading={eventBindingsLoading}
          eventBindingSavingByType={eventBindingSavingByType}
          getBindingKey={getBindingKey}
          getBindingOptions={getBindingOptions}
          onSetEventBinding={handleSetEventBinding}
          onClearEventBinding={handleClearEventBinding}
          onOpenTemplate={handleOpenEventBindingTemplate}
        />
        )}

        {sectionId === 'runtime' && (
        <EventBindingsRuntimeCard
          embedded
          tenantIdForApi={tenantIdForApi}
          isCollapsed={false}
          onToggleCollapse={() => undefined}
          eventBindings={eventBindings}
          eventBindingsLoading={eventBindingsLoading}
          templatesById={templatesById}
          onOpenTemplate={handleOpenEventBindingTemplate}
        />
        )}

        {sectionId === 'cluster' && (
        <ClusterFollowupBindingsOverviewCard
          embedded
          tenantIdForApi={tenantIdForApi}
          clusters={clustersForBindings}
          isLoading={clusterUsageLoading}
          onBindingsChanged={refreshClusterUsage}
        />
        )}

        {sectionId === 'list' && (
        <TemplatesTable
          isLoading={isLoading}
          isSuperAdmin={isSuperAdmin}
          selectedTenantId={selectedTenantId || ''}
          templates={filteredTemplates}
          selectedIds={selectedIds}
          isBulkLoading={isBulkLoading}
          hasActiveFilters={hasActiveFilters}
          canRead={can('templates:read')}
          canUpdate={can('templates:update')}
          canDelete={can('templates:delete')}
          getTenantName={getTenantName}
          getScenarioBadges={getScenarioBadges}
          getDispatchModes={getDispatchModes}
          getRelatedAutoReplies={getRelatedAutoReplies}
          onSelectAll={handleSelectAll}
          onSelectOne={handleSelectOne}
          onBulkActivate={handleBulkActivate}
          onBulkDeactivate={handleBulkDeactivate}
          onClearSelection={() => setSelectedIds(new Set())}
          onCreate={() => setIsCreateModalOpen(true)}
          onPreview={handlePreview}
          onEdit={openEditModal}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
        />
        )}
        </div>

        <TemplateFormModal
          isOpen={isTemplateFormOpen}
          editingTemplate={editingTemplate}
          isSuperAdmin={isSuperAdmin}
          formData={formData}
          setFormData={setFormData}
          variables={variables}
          insertVariable={insertVariable}
          onClose={handleCloseTemplateForm}
          onSubmit={editingTemplate ? handleUpdate : handleCreate}
          isSubmitting={isCreating || isUpdating}
          isSubmitDisabled={isTemplateFormSubmitDisabled}
          whatsappType={whatsappType}
          setWhatsappType={setWhatsappType}
          showWabaForAll={showWabaForAll}
          setShowWabaForAll={setShowWabaForAll}
          wabaTemplates={wabaTemplates}
          isLoadingWabaTemplates={isLoadingWabaTemplates}
          selectedWabaTemplate={selectedWabaTemplate}
          setSelectedWabaTemplate={setSelectedWabaTemplate}
          wabaVariableMapping={wabaVariableMapping}
          setWabaVariableMapping={setWabaVariableMapping}
          wabaLoadError={wabaLoadError}
        />

        {/* Модалка превью */}
        <Modal
          isOpen={!!previewingTemplate}
          onClose={() => setPreviewingTemplate(null)}
          title="Превью шаблона"
          size="md"
          closeOnBackdropClick={false}
          testId="modal-preview-template"
        >
          {isPreviewing ? (
            <LoadingState text="Загрузка превью..." size="sm" inline />
          ) : previewData ? (
            <ResolvedTemplatePreview
              preview={previewData.preview}
              wabaPreview={previewData.waba_preview}
              buttons={previewData.buttons}
              variablesMissing={previewData.variables_missing}
              channel={previewTemplateEntity?.messenger || previewTemplateEntity?.channel || undefined}
              isWabaTemplate={Boolean(previewTemplateEntity && isWabaBackedTemplate(previewTemplateEntity))}
              testId="preview-modal-template-content"
            />
          ) : null}
        </Modal>
      </div>
    </MainLayout>
  );
}
